import { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import Cookies from "js-cookie";

const API = process.env.REACT_APP_API_BASE_URL;
const STORAGE = process.env.REACT_APP_STORAGE_URL;

interface Product {
  id: number;
  name: string;
  product_code: string;
  price: string;
  discount: string;
  final_price: number;
  commission_percent: string;
  images: { image: string }[];
}

export default function AffiliateCommissionPage() {
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [commission, setCommission] = useState("");
  const [showModal, setShowModal] = useState(false);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const calculateCommission = () => {
    if (!selectedProduct || !commission) return 0;

    const percent = Number(commission);
    const price = Number(selectedProduct.final_price);

    return (price * percent) / 100;
  };

  const rupiah = (value: number) => new Intl.NumberFormat("id-ID").format(value);

  const fetchProducts = async () => {
    try {
      const token = Cookies.get("token");

      const res = await axios.get(`${API}/api/produk`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          page,
        },
      });

      setProducts(res.data.data);
      setTotalPages(res.data.last_page);
    } catch {
      Swal.fire("Error", "Gagal mengambil produk", "error");
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [page]);

  const openModal = (product: Product) => {
    setSelectedProduct(product);
    setCommission(product.commission_percent);
    setShowModal(true);
  };

  const handleUpdateCommission = async () => {
    if (!selectedProduct) return;

    const token = Cookies.get("token");

    try {
      await axios.put(
        `${API}/api/admin/products/${selectedProduct.id}/commission`,
        {
          commission_percent: commission,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      Swal.fire("Berhasil", "Komisi berhasil diperbarui", "success");

      setShowModal(false);
      fetchProducts();
    } catch {
      Swal.fire("Error", "Gagal update komisi", "error");
    }
  };

  return (
    <div className="mt-7">
      <h1 className="text-2xl font-bold mb-6">Management Komisi Affiliate</h1>

      {/* PRODUCT GRID */}
      <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
        {products.map((p) => (
          <div key={p.id} className="bg-white rounded-xl shadow hover:shadow-lg transition overflow-hidden">
            {/* IMAGE */}
            <img src={`${STORAGE}/products/${p.images?.[0]?.image}`} alt="" className="w-full h-48 object-cover" />

            <div className="p-4">
              <h3 className="font-semibold text-lg line-clamp-1">{p.name}</h3>

              <p className="text-sm text-gray-500">{p.product_code} </p>

              <div className="mt-2 text-sm">
                <p>
                  Harga: <span className="font-semibold">Rp {new Intl.NumberFormat("id-ID").format(Number(p.price))}</span>
                </p>

                <p>
                  Diskon: <span className="text-red-500">{p.discount}%</span>
                </p>

                <p>
                  Final: <span className="font-bold text-green-600">Rp {new Intl.NumberFormat("id-ID").format(Number(p.final_price))}</span>
                </p>
              </div>

              <div className="mt-3 flex items-center justify-between">
                <span className="text-sm bg-yellow-100 text-yellow-700 px-2 py-1 rounded">Komisi {p.commission_percent}%</span>

                <button onClick={() => openModal(p)} className="bg-primary text-white px-3 py-1 rounded-lg text-sm">
                  Set Komisi
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* PAGINATION */}
      <div className="flex justify-end gap-2 mt-6">
        {[...Array(totalPages)].map((_, i) => (
          <button key={i} onClick={() => setPage(i + 1)} className={`px-3 py-1 rounded ${page === i + 1 ? "bg-primary text-white" : "bg-gray-200"}`}>
            {i + 1}
          </button>
        ))}
      </div>

      {/* MODAL */}
      {showModal && selectedProduct && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center">
          <div className="bg-white rounded-xl p-6 w-[350px]">
            <h2 className="text-xl font-bold mb-4">Set Komisi</h2>

            <p className="text-sm mb-3">
              Produk: <b>{selectedProduct.name}</b>
            </p>

            <input type="number" value={commission} onChange={(e) => setCommission(e.target.value)} className="w-full border p-2 rounded" placeholder="Komisi %" />

            {/* HASIL KOMISI */}
            {commission && (
              <div className="mt-3 text-sm bg-green-50 border border-green-200 p-3 rounded-lg">
                <p>
                  Komisi {commission}% dari harga <b>Rp {rupiah(Number(selectedProduct.final_price))}</b>
                </p>

                <p className="font-bold text-green-600 mt-1">= Rp {rupiah(calculateCommission())}</p>
              </div>
            )}

            <div className="flex gap-3 mt-5">
              <button onClick={() => setShowModal(false)} className="w-full bg-gray-200 py-2 rounded">
                Batal
              </button>

              <button onClick={handleUpdateCommission} className="w-full bg-primary text-white py-2 rounded">
                Update
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
